"use client"

import { useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { useLocale } from "next-intl"
import { Button } from "@/components/ui/button"
import { useCountry } from "@/components/layout/country-provider"
import { useTranslations } from "@/lib/intl/use-translations"
import { ArrowRightIcon } from "@/components/ui/button"
import { insuranceKeys as ik } from "@/i18n/insurance-keys"
import {
  insurerPageSlugFor,
  isInsurerPageSlug,
} from "@/lib/insurance/insurer-pages"
import { useLocalePath } from "@/lib/use-localized-navigation"
import type { InsurerData } from "@/lib/insurance/insurance-types"

// `brand` is the insurer's name on its own (no plan, no product line): the
// per-card "Learn more about KPT coverage" CTA reads the brand, never the
// plan-bearing title ("KPT Pulse"). Brand names are not translated.
// `insurerId` is the CMS insurer id, used to find the insurer's own page.
const INSURER_EXAMPLES = [
  {
    nameKey: ik.cssName,
    brand: "CSS",
    insurerId: "css",
    coverageNameKey: ik.cssCoverageName,
    descKey: ik.cssDescription,
    coverageDescKey: ik.cssCoverageDescription,
    logo: "/icons/css-color.svg",
  },
  {
    nameKey: ik.kptName,
    brand: "KPT",
    insurerId: "kpt",
    descKey: ik.kptDescription,
    coverageDescKey: ik.kptCoverageDescription,
    logo: "/icons/kpt-color.svg",
  },
  {
    nameKey: ik.visanaName,
    brand: "Visana",
    insurerId: "visana",
    descKey: ik.visanaDescription,
    coverageDescKey: ik.visanaCoverageDescription,
    logo: "/icons/visana-color.svg",
  },
  {
    nameKey: ik.atupriName,
    brand: "Atupri",
    insurerId: "atupri",
    descKey: ik.atupriDescription,
    coverageDescKey: ik.atupriCoverageDescription,
    logo: "/icons/atupri-color.svg",
  },
  {
    nameKey: ik.axaName,
    brand: "AXA",
    insurerId: "axa",
    coverageNameKey: ik.axaCoverageName,
    descKey: ik.axaDescription,
    coverageDescKey: ik.axaCoverageDescription,
    logo: "/icons/axa-text.svg",
  },
  {
    nameKey: ik.groupeMutuelName,
    brand: "Groupe Mutuel",
    insurerId: "groupe-mutuel",
    descKey: ik.groupeMutuelDescription,
    coverageDescKey: ik.groupeMutuelCoverageDescription,
    logo: "/icons/groupe-mutuel.svg",
    logoOffsetY: 3,
  },
  {
    nameKey: ik.swicaName,
    brand: "SWICA",
    insurerId: "swica",
    descKey: ik.swicaDescription,
    coverageDescKey: ik.swicaCoverageDescription,
    logo: "/icons/swica-color.png",
  },
  {
    nameKey: ik.egkName,
    brand: "EGK",
    insurerId: "egk",
    descKey: ik.egkDescription,
    coverageDescKey: ik.egkCoverageDescription,
    logo: "/icons/egk-color.png",
  },
] as const

// KPT publishes this page per language, so send visitors to the version that
// matches the locale they're reading — a de-only href dropped EN/FR readers on
// a German page.
// No `nl` entry on purpose: KPT is a Swiss insurer and has no Dutch page, so
// the card just renders unlinked there rather than pointing at a German one.
const KPT_HREF_BY_LOCALE: Record<string, string | undefined> = {
  de: "https://www.kpt.ch/de/ambulante-zusatzversicherungen",
  en: "https://www.kpt.ch/en/supplementary-outpatient-insurance",
  fr: "https://www.kpt.ch/fr/assurance-complementaire-ambulatoire",
}

// Groupe Mutuel, SWICA and EGK are the insurers the non-CH locales deliberately
// don't carry — `SWISS_INSURER_KEYS` in `scripts/lint-mirror-locales.ts` drops
// `insurance.groupeMutuel*`, `insurance.swica*` and `insurance.egk*` from
// `de-DE.json`, `de-AT.json`, `nl.json` and `en-NL.json`, because Ahead is a
// pure self-pay service in those markets and their localization rules don't
// name these Swiss insurers. The component rendered them unconditionally, so
// `/de/de/go/insurancemri` threw `MISSING_MESSAGE: insurance.swicaName` in
// production (Sentry MARKETING-SITE-12D). Skip the cards on the markets whose
// message file has no copy for them rather than reintroducing the keys.
//
// `nl` joined the set on 2026-08-08: the NL catalogs still carried the keys, so
// the SWICA and EGK reimbursement copy — Swiss francs, in Dutch — shipped in
// every NL page's RSC payload even though no NL page renders the cards. Every
// EUR market is now in the set, leaving CH as the only market these render for.
const MARKETS_WITHOUT_SWISS_INSURER_COPY = new Set(["de", "at", "nl"])
const SWISS_ONLY_INSURER_NAME_KEYS = new Set<string>([
  ik.groupeMutuelName,
  ik.swicaName,
  ik.egkName,
])

/* The eight cards above are the insurers that reimburse. The remaining six
   confirmed no coverage, so they have no figures to quote — they still get a
   card (behind "View all", with their own page linked) because their pages
   are otherwise unreachable from the hub, and "does X cover this?" is exactly
   what people search. Logos come from the CMS, which carries all fourteen. */
const NO_COVERAGE_INSURERS = [
  { id: "assura", name: "Assura", slug: "assura" },
  { id: "concordia", name: "Concordia", slug: "concordia" },
  { id: "helsana", name: "Helsana", slug: "helsana" },
  { id: "oekk", name: "ÖKK", slug: "oekk" },
  { id: "sanitas", name: "Sanitas", slug: "sanitas" },
  { id: "sympany", name: "Sympany", slug: "sympany" },
] as const

// Two copy variants, selected per call site.
//
// Default (`/go/insurancemri`): the shared marketing summary — `nameKey` +
// `descKey`, plan-specific titles like "CSS myFlex Premium".
//
// `coveragePageCopy` (`/insurance` only): the precise reimbursement terms —
// percentages, per-calendar-year caps and exclusions — plus brand-only CSS and
// AXA titles, because that copy spans several plans ("depending on plan",
// "Caps vary by plan") and a title naming one plan contradicts it.
//
// Opt-in rather than a change to the shared data, so editing the coverage page's
// figures can never silently rewrite the ad LPs (which is exactly what happened
// when the two shared one set of keys). Cards fall back to the shared copy for
// any variant they don't define.
//
// The section heading follows the same switch: `/insurance` renders
// `examplesCoverageHeading`, the LPs keep `examplesHeading`.
//
// On the coverage page a card also carries a "Learn more about X coverage"
// CTA — `/insurance` is the hub for the per-insurer pages
// (`/insurance/<insurer>`, `lib/insurance/insurer-pages.ts`) — but only while
// that insurer's page is past its `released` gate. `linkInsurerPages` turns
// the same CTA on without the coverage copy, for the `/go/insurance*` ad LPs.
export function InsurerExamples({
  coveragePageCopy = false,
  linkInsurerPages = coveragePageCopy,
  /** CMS insurer rows — only used to source the logos of the six that don't
   *  reimburse, so the "View all" set doesn't need eight more icon files. */
  insurerData = null,
}: {
  coveragePageCopy?: boolean
  /** Show each card's "Learn more about X coverage" link to its
   *  `/insurance/<insurer>` page. On by default with `coveragePageCopy`. */
  linkInsurerPages?: boolean
  insurerData?: InsurerData | null
} = {}) {
  const [showAll, setShowAll] = useState(false)
  const t = useTranslations("insurance")
  const tp = useTranslations("insurancePages")
  const lp = useLocalePath()
  const locale = useLocale()
  const { code: country } = useCountry()

  const insurers = MARKETS_WITHOUT_SWISS_INSURER_COPY.has(country)
    ? INSURER_EXAMPLES.filter(
        (example) => !SWISS_ONLY_INSURER_NAME_KEYS.has(example.nameKey)
      )
    : INSURER_EXAMPLES

  // These cards exist to link to the insurer's own page — nothing else on
  // them is a reason to be on the hub. While the pages are behind the
  // `released` gate the list is empty, so neither the cards nor the
  // "View all insurers" button render at all.
  const noCoverageInsurers = NO_COVERAGE_INSURERS.filter((insurer) =>
    isInsurerPageSlug(insurer.slug)
  )

  return (
    <section className="py-16">
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <div>
          <h3 className="type-display-sm text-neutral-900">
            {t(
              coveragePageCopy ? ik.examplesCoverageHeading : ik.examplesHeading
            )}
          </h3>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
          {insurers.map((example, index) => {
            const coverageNameKey =
              "coverageNameKey" in example ? example.coverageNameKey : undefined
            const name = t(
              coveragePageCopy && coverageNameKey
                ? coverageNameKey
                : example.nameKey
            )
            const descKey =
              coveragePageCopy && "coverageDescKey" in example
                ? example.coverageDescKey
                : example.descKey
            const href =
              example.nameKey === ik.kptName
                ? KPT_HREF_BY_LOCALE[locale]
                : undefined
            const offsetY =
              "logoOffsetY" in example ? example.logoOffsetY : undefined
            // Undefined while the insurer's page is behind the `released`
            // gate — the CTA then has nowhere to go, so it isn't rendered.
            const pageSlug = insurerPageSlugFor(example.insurerId)
            const logoTile = (
              <div className="flex h-[90px] w-[90px] flex-shrink-0 items-center justify-center rounded-[12px] bg-neutral-50 p-4">
                <Image
                  src={example.logo}
                  alt={name}
                  width={62}
                  height={62}
                  style={
                    offsetY
                      ? { transform: `translateY(${offsetY}px)` }
                      : undefined
                  }
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            )
            const heading = (
              <h4 className="type-title-lg text-neutral-900">{name}</h4>
            )
            return (
              <div key={index}>
                <div className="flex h-full flex-col gap-6 rounded-[16px] bg-white p-6 md:pt-10 md:pr-[60px] md:pb-10 md:pl-10">
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={name}
                      className="w-fit transition-opacity hover:opacity-80"
                    >
                      {logoTile}
                    </a>
                  ) : (
                    logoTile
                  )}
                  <div>
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-fit transition-colors hover:text-neutral-700"
                      >
                        {heading}
                      </a>
                    ) : (
                      heading
                    )}
                    <p className="type-body-lg mt-2 text-neutral-600">
                      {t(descKey)}
                    </p>
                  </div>
                  {linkInsurerPages && pageSlug && (
                    <Link
                      href={lp(`/insurance/${pageSlug}`)}
                      className="group type-title-md mt-auto inline-flex w-fit items-center gap-3 font-medium text-neutral-900"
                    >
                      {t(ik.learnMoreAbout, { name: example.brand })}
                      <span
                        aria-hidden="true"
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-900 transition-colors group-hover:bg-neutral-200"
                      >
                        <ArrowRightIcon size={24} />
                      </span>
                    </Link>
                  )}
                </div>
              </div>
            )
          })}
          {/* The insurers that confirmed no coverage. Rendered into the DOM
              either way so their pages stay crawlable from the hub; the class
              only hides them until "View all". */}
          {coveragePageCopy &&
            noCoverageInsurers.map((insurer) => {
              const logo = insurerData?.insurers.find(
                (row) => row.id === insurer.id
              )?.logo?.url
              // One sentence per insurer, so six cards in a row don't repeat
              // the same line. No copy written yet → no paragraph at all,
              // same as the package prose on the per-insurer pages.
              // no-raw-ids: a message-catalog key path, never rendered as text
              const noCoverageKey = `insurers.${insurer.id}.noCoverageSummary`
              return (
                <div
                  key={insurer.id}
                  className={showAll ? "" : "hidden"}
                  aria-hidden={showAll ? undefined : true}
                >
                  <div className="flex h-full flex-col gap-6 rounded-[16px] bg-white p-6 md:pt-10 md:pr-[60px] md:pb-10 md:pl-10">
                    <div className="flex h-[90px] w-[90px] flex-shrink-0 items-center justify-center rounded-[12px] bg-neutral-50 p-4">
                      {logo && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={logo}
                          alt={insurer.name}
                          width={62}
                          height={62}
                          className="max-h-full max-w-full object-contain"
                        />
                      )}
                    </div>
                    <div>
                      <h4 className="type-title-lg text-neutral-900">
                        {insurer.name}
                      </h4>
                      {tp.has(noCoverageKey as never) && (
                        <p className="type-body-lg mt-2 text-neutral-600">
                          {tp(noCoverageKey as never)}
                        </p>
                      )}
                    </div>
                    <Link
                      href={lp(`/insurance/${insurer.slug}`)}
                      className="group type-title-md mt-auto inline-flex w-fit items-center gap-3 font-medium text-neutral-900"
                    >
                      {t(ik.learnMoreAbout, { name: insurer.name })}
                      <span
                        aria-hidden="true"
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-900 transition-colors group-hover:bg-neutral-200"
                      >
                        <ArrowRightIcon size={24} />
                      </span>
                    </Link>
                  </div>
                </div>
              )
            })}

          {coveragePageCopy && !showAll && noCoverageInsurers.length > 0 && (
            <div className="flex justify-center md:col-span-2">
              <Button variant="outline" onClick={() => setShowAll(true)}>
                {tp("viewAllInsurers")}
              </Button>
            </div>
          )}

          {/* "Different plan" CTA card */}
          <div className="md:col-span-2">
            <div className="flex h-full flex-col items-center justify-center gap-4 rounded-[16px] bg-white p-6 text-center md:pt-10 md:pr-[60px] md:pb-10 md:pl-10">
              <p className="type-body-lg text-neutral-600">
                {t(ik.differentPlanText)}
                <br />
                {t(ik.differentPlanQualify)}
              </p>
              <Link href={lp("/contact")}>
                <Button variant="outline">{t(ik.talkToTeam)}</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
